import React, { useEffect, useState, useRef } from "react";
import PremiumBlock from "@/components/Premium/PremiumBlock";
import { useQuery } from "@tanstack/react-query";
import PremiumFooter from "@/components/Premium/PremiumFooter";
import { useNavigate, useParams } from "react-router-dom";
import { getPlatform, getProducts, makePurchase, Product } from "@/lib/iap";
import { useToast, CloseButton } from "@chakra-ui/react";
import { callWithAuth, getUserInfo } from "@/api/Api";
import { LoadingPage } from "./LoadingPage";
import NoPaymentText from "@/components/NoPaymentText";
import FreeTrialStepper from "@/components/Premium/FreeTrialStepper";
import PremiumFeatures from "@/components/Premium/PremiumFeatures";
import { BellIcon } from "@chakra-ui/icons";
import { usePostHog } from "posthog-js/react";
import { OnboardingStepMap, OnboardingAnalyticsKey } from "./OnboardingPage";
import { FREE_TRIAL_DAYS } from "@/lib/domainConstants";
// Define types for components
function useProducts() {
  const [amount, setAmount] = useState("");
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  useEffect(() => {
    if (productsLoaded) {
      return;
    }
    setProductsLoaded(true);
    getProducts().then((products) => {
      setProducts(products);
      setAmount(products[1].price);
    });
  }, [productsLoaded]);
  return { products, productsLoaded, amount, setAmount };
}

const PLATFORM = getPlatform();

const PremiumPage: React.FC = () => {
  const { "*": backUrl } = useParams();
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState("Monthly");
  const [selectedSku, setSelectedSku] = useState("monthly_sub");
  const { products, productsLoaded, amount, setAmount } = useProducts();
  const toast = useToast();
  const [isSubscribing, setIsSubscribing] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [isBellAnimating, setIsBellAnimating] = useState(true);
  const runOnce = useRef(false);
  const posthog = usePostHog();

  useEffect(() => {
    if (runOnce.current) return;
    runOnce.current = true;
    posthog.capture("premium_page_viewed");
  }, []);

  const userInfoQuery = useQuery({
    queryKey: ["userInfo"],
    queryFn: () => callWithAuth(getUserInfo),
  });
  const isTemp =
    userInfoQuery.data?.isTemp ||
    userInfoQuery.data?.isTempTrial ||
    userInfoQuery.data?.isTempPremium;

  const isFreeTrialUsed = userInfoQuery.data?.isFreeTrialUsed || false;

  const handlePaymentClose = () => {
    setIsSubscribing(false);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  useEffect(() => {
    // Attach the event listener
    window.addEventListener("payment-window-closed", handlePaymentClose);

    // Return a cleanup function to remove the event listener
    return () => {
      window.removeEventListener("payment-window-closed", handlePaymentClose);
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsBellAnimating(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const handleSubscribe = async () => {
    if (isSubscribing) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      setIsSubscribing(false);
      return;
    }
    setIsSubscribing(true);

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      if (selectedSku && PLATFORM === "apple") {
        const isSuccess = await makePurchase(
          selectedSku,
          abortController.signal
        );
        if (isSuccess === null) {
          return;
        }
        if (!isSuccess) {
          toast({
            title: "Subscription Error",
            description:
              "Something went wrong, please try again or contact me at 778-228-6108 and I will personally fix your problem. - Erik Dahl Parrot Founder",
            status: "error",
            duration: 10000,
            isClosable: true,
            position: "top",
          });
        } else {
          if (isTemp) {
            navigate("/signup");
            return;
          } else {
            if (userInfoQuery.data?.firstName == "") {
              navigate("/name");
            } else {
              navigate("/placement");
            }
            return;
          }
        }
      } else if (selectedSku && PLATFORM === "web") {
        if (backUrl) {
          navigate(`/checkout/${backUrl}?sku=${selectedSku}`);
        } else {
          navigate(`/checkout/signup?sku=${selectedSku}`);
        }
      }
    } catch (error: unknown) {
      if (error instanceof Error && error.name === "AbortError") {
        console.log("Subscription process was canceled");
        return;
      } else {
        console.error("Subscription error:", error);
      }
    } finally {
      setIsSubscribing(false);
      abortControllerRef.current = null;
    }
  };

  if (!productsLoaded) {
    return <LoadingPage />;
  }

  if (userInfoQuery.isPending) {
    return <LoadingPage />;
  }

  if (isSubscribing) {
    return <LoadingPage />;
  }

  const useMonthlyFreeTrialUI = selectedPlan === "Monthly" && !isFreeTrialUsed;

  return (
    <div className="appPadding flex flex-col items-center justify-between w-full h-full overflow-hidden">
      <CloseButton
        onClick={() =>
          navigate(
            `/onboarding/${
              OnboardingStepMap[OnboardingAnalyticsKey.PLAN_LOADED]
            }`
          )
        }
        position="absolute"
        top={3}
        right={4}
        textColor={"var(--color-grey)"}
        opacity={0.7}
        zIndex={1000}
      />
      <div className="flex flex-col flex-grow justify-start gap-4 overflow-y-auto w-full h-full z-40">
        <div className="text-center text-3xl font-extrabold pt-6">
          {useMonthlyFreeTrialUI
            ? `Start your ${FREE_TRIAL_DAYS}-day FREE trial to continue`
            : "Unlock Parrot to learn Spanish faster"}
        </div>
        {useMonthlyFreeTrialUI && (
          <div className="flex flex-row items-center justify-center gap-2 text-center mt-2">
            <div className="relative flex justify-center items-center">
              <BellIcon
                boxSize={6}
                className={`text-[var(--color-grey)] ${
                  isBellAnimating ? "animate-bell-bounce" : ""
                }`}
              />
              <div className="absolute top-1 right-1 bg-[var(--color-red)] rounded-full w-[5px] h-[5px] flex items-center justify-center animate-badge-appear"></div>
            </div>
            We'll remind you before the trial ends
          </div>
        )}
        <div className="flex flex-grow w-full justify-center items-center">
          {useMonthlyFreeTrialUI ? <FreeTrialStepper /> : <PremiumFeatures />}
        </div>
        <div className="flex flex-row gap-4">
          {products.map((product) => (
            <PremiumBlock
              key={product.sku}
              currency={product.currency}
              heading={product.name}
              price={
                product.name === "Monthly"
                  ? (
                      Math.floor((Number(product.price) / 4) * 100) / 100
                    ).toFixed(2)
                  : product.price
              }
              // This one always needs to be there, not dependong on selected plan
              isFreeTrial={product.name === "Monthly" && !isFreeTrialUsed}
              freeTrialDays={FREE_TRIAL_DAYS}
              onClick={() => {
                setAmount(product.price);
                setSelectedPlan(product.name);
                setSelectedSku(product.sku);
              }}
              selected={selectedPlan === product.name}
            />
          ))}
        </div>
        {selectedPlan === "Monthly" && !isFreeTrialUsed ? (
          <NoPaymentText />
        ) : (
          <div className="h-6"></div>
        )}
      </div>
      <div className="w-full">
        <PremiumFooter
          platform={PLATFORM}
          amount={amount}
          period={selectedPlan === "Weekly" ? "weekly" : "monthly"}
          showFreeTrialText={useMonthlyFreeTrialUI}
          onContinue={handleSubscribe}
          currency={
            products.find((product) => product.sku === selectedSku)?.currency ||
            "USD"
          }
        />
      </div>
    </div>
  );
};

export default PremiumPage;
